import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, inr } from "../lib/api";
import TopBar from "../components/TopBar";
import { Button, Card, Input, Select, Textarea } from "../components/ui";
import { ArrowLeft, Receipt } from "lucide-react";

const CATEGORIES = [
  "ground", "ball", "water", "snacks", "jersey", "transport", "trophy", "umpire", "other",
];

export default function ExpenseNewPage() {
  const nav = useNavigate();
  const loc = useLocation();
  const preMatchId = new URLSearchParams(loc.search).get("match");

  const [members, setMembers] = useState([]);
  const [matches, setMatches] = useState([]);
  const [form, setForm] = useState({
    amount: "",
    category: "ground",
    description: "",
    paid_by: "",
    match_id: preMatchId || "",
    sponsor_contribution: 0,
    outside_contribution: 0,
    included_members: [],
    split_equally: true,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: mem }, { data: mts }] = await Promise.all([
          api.get("/members"),
          api.get("/matches"),
        ]);
        setMembers(mem);
        setMatches(mts);
        setForm((f) => ({ ...f, paid_by: mem[0]?.id || "", included_members: mem.map((x) => x.id) }));
      } catch (err) { toast.error(apiError(err)); }
    })();
  }, []);

  useEffect(() => {
    if (!form.match_id) return;
    const m = matches.find((x) => x.id === form.match_id);
    if (m) {
      const ids = Array.from(new Set([...(m.playing_xi || []), ...(m.guests || [])]));
      if (ids.length) setForm((f) => ({ ...f, included_members: ids }));
    }
    // eslint-disable-next-line
  }, [form.match_id]);

  const toggleMember = (id) => {
    setForm((f) => ({
      ...f,
      included_members: f.included_members.includes(id)
        ? f.included_members.filter((x) => x !== id)
        : [...f.included_members, id],
    }));
  };

  const perHead = useMemo(() => {
    const net = Number(form.amount || 0) - Number(form.sponsor_contribution || 0) - Number(form.outside_contribution || 0);
    if (form.included_members.length === 0 || net <= 0) return 0;
    return Math.round((net / form.included_members.length) * 100) / 100;
  }, [form.amount, form.sponsor_contribution, form.outside_contribution, form.included_members]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.paid_by) { toast.error("Select who paid"); return; }
    setSaving(true);
    try {
      await api.post("/expenses", {
        amount: Number(form.amount),
        category: form.category,
        description: form.description,
        paid_by: form.paid_by,
        match_id: form.match_id || null,
        sponsor_contribution: Number(form.sponsor_contribution) || 0,
        outside_contribution: Number(form.outside_contribution) || 0,
        included_members: form.included_members,
        split_equally: true,
      });
      toast.success("Expense recorded, ledger updated");
      if (form.match_id) nav(`/matches/${form.match_id}`);
      else nav("/ledger");
    } catch (err) {
      toast.error(apiError(err));
    } finally { setSaving(false); }
  };

  return (
    <div className="pb-24">
      <div className="flex items-center px-3 pt-4">
        <button onClick={() => nav(-1)} className="h-10 w-10 rounded-full hover:bg-black/5 grid place-items-center" data-testid="back-btn">
          <ArrowLeft size={20} />
        </button>
      </div>
      <TopBar title="Add Expense" subtitle="Splits update every ledger" />
      <form onSubmit={submit} className="px-5 space-y-4">
        <Card className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Input label="Amount ₹ *" type="number" min="0" step="0.01" value={form.amount} onChange={(e)=>setForm({...form, amount:e.target.value})} required data-testid="exp-amount" />
            <Select label="Category" value={form.category} onChange={(e)=>setForm({...form, category:e.target.value})} data-testid="exp-category">
              {CATEGORIES.map((c) => <option key={c} value={c} className="capitalize">{c}</option>)}
            </Select>
          </div>
          <Input label="Description" placeholder="e.g. New leather ball" value={form.description} onChange={(e)=>setForm({...form, description:e.target.value})} data-testid="exp-description" />
          <Select label="Paid by *" value={form.paid_by} onChange={(e)=>setForm({...form, paid_by:e.target.value})} required data-testid="exp-paid-by">
            <option value="">Select member</option>
            {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </Select>
          <Select label="Match (optional)" value={form.match_id} onChange={(e)=>setForm({...form, match_id:e.target.value})} data-testid="exp-match">
            <option value="">— None —</option>
            {matches.map((m) => <option key={m.id} value={m.id}>vs {m.opponent} • {m.date}</option>)}
          </Select>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Sponsor ₹" type="number" min="0" step="0.01" value={form.sponsor_contribution} onChange={(e)=>setForm({...form, sponsor_contribution:e.target.value})} data-testid="exp-sponsor" />
            <Input label="Outside ₹" type="number" min="0" step="0.01" value={form.outside_contribution} onChange={(e)=>setForm({...form, outside_contribution:e.target.value})} data-testid="exp-outside" />
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs uppercase tracking-widest text-ink-muted font-semibold">
              Split equally among ({form.included_members.length})
            </div>
            <div className="text-sm font-bold text-brand">{inr(perHead)}/head</div>
          </div>
          <div className="grid grid-cols-2 gap-2 max-h-72 overflow-y-auto pr-1">
            {members.map((m) => {
              const active = form.included_members.includes(m.id);
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => toggleMember(m.id)}
                  data-testid={`exp-member-${m.id}`}
                  className={`h-11 px-3 rounded-2xl text-sm font-semibold text-left truncate ${active ? "bg-brand text-white" : "bg-black/[0.04] text-ink"}`}
                >
                  {m.name}
                </button>
              );
            })}
          </div>
        </Card>

        <Button type="submit" size="lg" className="w-full" disabled={saving} data-testid="exp-submit">
          <Receipt size={18} />
          {saving ? "Saving…" : `Record expense ${form.amount ? "of " + inr(form.amount) : ""}`}
        </Button>
      </form>
    </div>
  );
}
